import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api';
import { Button, Badge, Modal, Alert, SearchBar, Spinner, InfoRow, Pagination } from './shared';

const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

const STATUS_OPTIONS = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'ready', label: 'Ready' },
    { value: 'expired', label: 'Expired' },
    { value: 'failed', label: 'Failed' },
];

const SOURCE_OPTIONS = [
    { value: '', label: 'All Sources' },
    { value: 'upload', label: 'Upload' },
    { value: 'external', label: 'External' },
];

const STATUS_COLORS = {
    ready: 'success',
    pending: 'warning',
    expired: 'outline',
    failed: 'error',
};

const DEFAULT_LIMIT = 50;

export default function AdminContentUrls() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [userIdFilter, setUserIdFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    const loadData = useCallback(async (newOffset) => {
        const currentOffset = newOffset !== undefined ? newOffset : offset;
        setLoading(true);
        setError(null);
        try {
            const params = { limit, offset: currentOffset };
            if (userIdFilter.trim()) params.user_id = userIdFilter.trim();
            if (statusFilter) params.status = statusFilter;
            if (sourceFilter) params.source_type = sourceFilter;
            if (searchQuery.trim()) params.search = searchQuery.trim();
            const res = await adminApi.listContentUrls(params);
            setItems(res.items || []);
            setTotal(res.total || 0);
        } catch (err) {
            setError(err.message || 'Failed to load content URLs');
        } finally {
            setLoading(false);
        }
    }, [userIdFilter, statusFilter, sourceFilter, searchQuery, offset, limit]);

    useEffect(() => { loadData(0); setOffset(0); }, [statusFilter, sourceFilter, limit]);
    useEffect(() => { loadData(); }, [offset]);

    const handleSearch = () => { setOffset(0); loadData(0); };

    const handlePageChange = (newOffset) => { setOffset(newOffset); };

    const handleLimitChange = (newLimit) => { setLimit(newLimit); setOffset(0); };

    const handleViewItem = async (id) => {
        try {
            const item = await adminApi.getContentUrl(id);
            setSelectedItem(item);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDownload = async (id) => {
        try {
            const res = await adminApi.getContentUrlDownload(id);
            window.open(res.download_url, '_blank');
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm(`Delete content URL #${id}?`)) return;
        try {
            await adminApi.deleteContentUrl(id);
            setSuccessMessage(`Deleted #${id}`);
            setSelectedItem(null);
            setItems(prev => prev.filter(i => i.id !== id));
            setTotal(prev => Math.max(0, prev - 1));
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const selectCls = "h-9 px-3 pr-8 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat";

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Content URLs</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {total} file{total !== 1 ? 's' : ''} — uploaded and external content
                    </p>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSearch={handleSearch}
                    onRefresh={() => loadData(offset)}
                    placeholder="Search filename or URL..."
                    className="flex-1"
                />
                <input
                    type="text"
                    value={userIdFilter}
                    onChange={(e) => setUserIdFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="User ID"
                    className="h-9 w-28 px-3 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls}>
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={selectCls}>
                    {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No content URLs found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 w-12">ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">File</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-24">Type</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Size</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Source</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Status</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-20">User</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-32">Created</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((item) => (
                                <tr
                                    key={item.id}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => handleViewItem(item.id)}
                                >
                                    <td className="px-4 py-2.5">
                                        <span className="text-xs text-gray-500 font-mono">{item.id}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs font-medium text-gray-900 truncate block max-w-xs">
                                            {item.original_filename || item.content_url}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-500 truncate block max-w-[100px]">
                                            {item.content_type || '-'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <span className="text-xs text-gray-500 font-mono">{formatSize(item.file_size)}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <Badge variant={item.source_type === 'upload' ? 'info' : 'outline'}>
                                            {item.source_type}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <Badge variant={STATUS_COLORS[item.status] || 'outline'}>
                                            {item.status}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        {item.owner_user_id
                                            ? <Badge variant="info">{item.owner_user_id}</Badge>
                                            : <span className="text-xs text-gray-400">-</span>
                                        }
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1">
                                            {item.s3_object_key && (
                                                <Button variant="ghost" size="xs" onClick={() => handleDownload(item.id)}>
                                                    Open
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost" size="xs"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 pb-3">
                        <Pagination total={total} limit={limit} offset={offset} onChange={handlePageChange} onLimitChange={handleLimitChange} />
                    </div>
                </div>
            )}

            {selectedItem && (
                <ContentUrlDetailModal
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    onDownload={() => handleDownload(selectedItem.id)}
                    onDelete={() => handleDelete(selectedItem.id)}
                />
            )}
        </div>
    );
}

function ContentUrlDetailModal({ item, onClose, onDownload, onDelete }) {
    return (
        <Modal open onClose={onClose} title={item.original_filename || `Content #${item.id}`} size="xl">
            <Modal.Body>
                <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                    <InfoRow label="ID" value={item.id} />
                    <InfoRow label="Status" value={item.status} />
                    <InfoRow label="Source" value={item.source_type} />
                    <InfoRow label="Content Type" value={item.content_type || '-'} />
                    <InfoRow label="File Size" value={formatSize(item.file_size)} />
                    <InfoRow label="Owner" value={item.owner_user_id || '-'} />
                    <InfoRow label="Access Count" value={item.access_count} />
                    <InfoRow label="Created" value={formatDate(item.created_at)} />
                    <InfoRow label="Updated" value={formatDate(item.updated_at)} />
                    <InfoRow label="Confirmed" value={formatDate(item.confirmed_at)} />
                    <InfoRow label="Expires" value={formatDate(item.expires_at)} />
                    <InfoRow label="Last Accessed" value={formatDate(item.last_accessed_at)} />
                </div>

                <div className="mt-3">
                    <InfoRow label="URL" value={item.content_url} mono />
                    {item.s3_object_key && <InfoRow label="S3 Key" value={item.s3_object_key} mono />}
                    {item.url_hash && <InfoRow label="URL Hash" value={item.url_hash} mono />}
                    {item.content_hash && <InfoRow label="Content Hash" value={item.content_hash} mono />}
                </div>

                {item.extracted_text && (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Extracted Text</p>
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[40vh]">
                            {item.extracted_text}
                        </pre>
                    </div>
                )}

                <div className="mt-4 flex justify-end gap-2">
                    {item.s3_object_key && (
                        <Button variant="primary" size="sm" onClick={onDownload}>
                            Open File
                        </Button>
                    )}
                    <Button
                        variant="ghost" size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={onDelete}
                    >
                        Delete
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}
